import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import type { BingDateGrain } from '../api/bingDashboard';
import type { AppLikeOverviewParams, AppLikeOverviewRow, AppLikeOverviewResponse } from '../api/applovinDashboard';
import { ColumnToggle } from '../components/ColumnToggle';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { alignMarketingDateRange, getCurrentCalendarRange, DELIVERY_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { SortableHeader, useTableSort, type SortValue } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import useAppStore from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

interface MetricField {
  key: keyof AppLikeOverviewRow;
  label: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

interface Props {
  storageKey: string;
  platformLabel: string;
  reportKey: string;
  supportedRegions: string[];
  getOverview: (params: AppLikeOverviewParams) => Promise<{ data: AppLikeOverviewResponse }>;
  metrics: MetricField[];
}

const DATE_GRAINS: Array<{ value: BingDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

export const fmtNum = (v: number, d = 2) =>
  v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
export const fmtPct = (v: number) => (v == null ? '-' : `${(v * 100).toFixed(2)}%`);

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function getPresetRange(preset: DatePreset, customStart: string, customEnd: string) {
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
}

function regionFromSiteCode(code: string): string {
  return String(code || '').split('-')[0]?.toLowerCase() || '';
}

export default function AppLikeDeliveryDashboard({ storageKey, platformLabel, reportKey, supportedRegions, getOverview, metrics }: Props) {
  const { brand, brandId, siteId, sites, adContext, sitesLoaded } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const [dateGrain, setDateGrain] = useState<BingDateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [appliedDate, setAppliedDate] = useState(() => ({ startDate: daysAgo(6), endDate: today(), dateGrain: 'day' as BingDateGrain }));
  const initialQueryKeyRef = useRef('');
  const [rows, setRows] = useState<AppLikeOverviewRow[]>([]);
  const [summary, setSummary] = useState<AppLikeOverviewRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore corrupted local preference
    }
    return new Set();
  });

  const currentSite = useMemo(() => {
    if (brand !== 'zeelool') return null;
    const site = sites.find(item => item.id === siteId);
    if (!site) return null;
    const region = regionFromSiteCode(site.code);
    if (!supportedRegions.includes(region)) return null;
    return { region, siteId: site.id, siteCode: site.code };
  }, [brand, siteId, sites, supportedRegions]);

  const dateRange = useMemo(
    () => alignMarketingDateRange(getPresetRange(datePreset, customStart, customEnd), dateGrain),
    [customEnd, customStart, dateGrain, datePreset],
  );

  useEffect(() => {
    if (!brandId || !currentSite?.siteId) return;
    const key = `${brandId}:${currentSite.siteId}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    setAppliedDate({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
  }, [brandId, currentSite?.siteId, dateGrain, dateRange.end, dateRange.start]);

  const visibleMetrics = useMemo(
    () => metrics.filter(m => !hiddenMetricKeys.has(m.key as string)),
    [hiddenMetricKeys, metrics],
  );

  const sortValueFor = useCallback((row: AppLikeOverviewRow, key: string): SortValue => {
    if (key !== 'date_label') {
      const metric = metrics.find(item => item.key === key);
      // Do not expose relative ordering for values that are currently masked.
      if (metric?.permKey && userLevel < 2) return undefined;
    }
    return row[key as keyof AppLikeOverviewRow] as SortValue;
  }, [metrics, userLevel]);
  const { sort, toggleSort, sortedRows, setSort } = useTableSort<AppLikeOverviewRow, string>(rows, sortValueFor);

  useEffect(() => {
    if (sort && sort.key !== 'date_label' && hiddenMetricKeys.has(sort.key)) setSort(null);
  }, [hiddenMetricKeys, setSort, sort]);

  const metricToggleCols = useMemo(() => metrics.map(m => ({ key: m.key as string, label: m.label })), [metrics]);

  const toggleMetricHidden = useCallback((key: string) => {
    setHiddenMetricKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, [storageKey]);

  const resetMetricHidden = useCallback(() => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem(storageKey); } catch {}
  }, [storageKey]);

  const fetchData = useCallback(async () => {
    if (!brandId || !currentSite?.siteId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getOverview({
        brand_id: brandId,
        site_id: currentSite.siteId,
        start_date: appliedDate.startDate,
        end_date: appliedDate.endDate,
        date_grain: appliedDate.dateGrain,
      });
      setRows(res.data?.list ?? []);
      setSummary(res.data?.summary ?? null);
    } catch (e) {
      setRows([]);
      setSummary(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [appliedDate, brandId, currentSite?.siteId, getOverview]);

  useEffect(() => {
    if (currentSite?.siteId) {
      fetchData();
    } else if (sitesLoaded) {
      setRows([]);
      setSummary(null);
    }
  }, [currentSite?.siteId, fetchData, sitesLoaded]);

  const query = () => setAppliedDate({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
  useMarketingViewSection('page', {
    label: '页面筛选', state: { dateGrain, datePreset, customStart, customEnd, hiddenMetricKeys: [...hiddenMetricKeys] },
    restore: value => { setDateGrain(value.dateGrain); setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); setHiddenMetricKeys(new Set(value.hiddenMetricKeys)); },
    loading: loading, query: () => { query(); },
    resolve: value => { const range = getPresetRange(value.datePreset, value.customStart, value.customEnd); return { ...value, customStart: range.start, customEnd: range.end }; },
  });

  const reset = () => {
    setDateGrain('day');
    setDatePreset('7d');
    setCustomStart(daysAgo(6));
    setCustomEnd(today());
    setAppliedDate({ startDate: daysAgo(6), endDate: today(), dateGrain: 'day' });
  };

  const renderMetric = (row: AppLikeOverviewRow, metric: MetricField) => {
    const raw = metric.fmt(row[metric.key] as number);
    if (!metric.permKey) return raw;
    return sf(raw, metric.permKey, userLevel).value ?? '锁定';
  };

  const handleExport = useCallback(async () => {
    if (!brandId || !currentSite?.siteId) return;
    try {
      await enqueueReportExport({
        report_key: reportKey,
        title: `${platformLabel}投放看板`,
        file_name: `${platformLabel}投放看板_${appliedDate.startDate}_${appliedDate.endDate}.csv`,
        brand_id: brandId,
        site_id: currentSite.siteId,
        params: {
          brand_id: brandId,
          site_id: currentSite.siteId,
          start_date: appliedDate.startDate,
          end_date: appliedDate.endDate,
          date_grain: appliedDate.dateGrain,
        },
        columns: [
          { key: 'date_label', label: '日期' },
          ...visibleMetrics.map(metric => ({ key: metric.key as string, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '导出任务提交失败', 'error');
    }
  }, [appliedDate, brandId, currentSite?.siteId, enqueueReportExport, platformLabel, reportKey, showToast, visibleMetrics]);

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <MarketingTimeRangeFilter grain={dateGrain} grains={DATE_GRAINS} onGrainChange={setDateGrain} preset={datePreset} presets={DELIVERY_RANGE_PRESETS} onPresetChange={setDatePreset} start={customStart} end={customEnd} max={today()} customPreset="custom" onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
          <DashboardQueryActions loading={loading} onReset={reset} onQuery={query} />
        </div>
      </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 6, background: 'rgba(220,38,38,.08)', color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ marginTop: 16, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>总览报表</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={handleExport}
              disabled={loading || !brandId || !currentSite}
              title="导出当前报表"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Download size={14} strokeWidth={2} />
              导出
            </button>
            <ColumnToggle cols={metricToggleCols} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetricHidden} onReset={resetMetricHidden} />
          </div>
        </div>
        <div className="table-wrap" style={{ position: 'relative', maxHeight: 'calc(100vh - 235px)', overflow: 'auto' }}>
          <table style={{ minWidth: 1200 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <SortableHeader
                  sortKey="date_label"
                  sort={sort}
                  onSort={toggleSort}
                  style={{ position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)', minWidth: 120 }}
                >
                  日期
                </SortableHeader>
                {visibleMetrics.map(metric => (
                  <SortableHeader
                    key={metric.key as string}
                    sortKey={metric.key as string}
                    sort={sort}
                    onSort={toggleSort}
                    align="right"
                    disabled={Boolean(metric.permKey && userLevel < 2)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <MetricDescriptionTooltip label={metric.label} />
                  </SortableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary && (
                <tr style={{ fontWeight: 700, background: 'var(--surface)' }}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)', color: 'var(--text2)' }}>总计</td>
                  {visibleMetrics.map(metric => (
                    <td key={metric.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>
                      {renderMetric(summary, metric)}
                    </td>
                  ))}
                </tr>
              )}
              {loading && rows.length === 0 ? (
                <tr><td colSpan={visibleMetrics.length + 1}><TableLoadingCell /></td></tr>
              ) : !currentSite ? (
                <tr><td colSpan={visibleMetrics.length + 1} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>请在左侧切换到 US 站点查看 {platformLabel} 投放数据</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={visibleMetrics.length + 1} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>暂无数据</td></tr>
              ) : sortedRows.map((row, idx) => (
                <tr key={`${row.date_label}-${idx}`}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: idx % 2 === 0 ? 'var(--surface2)' : 'var(--surface)', fontVariantNumeric: 'tabular-nums' }}>
                    {row.date_label}
                  </td>
                  {visibleMetrics.map(metric => (
                    <td key={metric.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {renderMetric(row, metric)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {loading && rows.length > 0 && <TableLoadingOverlay />}
        </div>
      </div>
    </div>
  );
}
